import { z } from 'zod';
import { centavos } from './envelope.ts';
import type { GameLaunchRefusal } from './games.ts';
import { withdrawalStepUp } from './player-two-factor.ts';

/**
 * THE MONEY ROUND TRIP, from the player's side.
 *
 * Its own file rather than more of `player-auth.ts`, which describes opening an
 * account and getting into it. Everything here is about MOVING money or reading
 * what moved, and the two have different failure modes: a bad registration
 * creates an account nobody wanted, a bad withdrawal request creates a claim on
 * cash.
 *
 * WHAT IS DELIBERATELY ABSENT, so nobody reads the file as unfinished:
 *
 *  · A `brandId`, and a `playerId`. Both are resolved from the session cookie
 *    server-side. A caller-named player on a withdrawal request is a caller
 *    naming whose money to move; there is no field here that could carry one.
 *
 *  · Any fee, limit or arrival estimate as an INPUT. Those are platform and
 *    rail configuration read from `payment.payment_method` and
 *    `platform.setting`; a client that could state them is a client that could
 *    state a different one from the server.
 *
 *  · The destination account NUMBER. A withdrawal names one of the player's
 *    registered payout accounts (`payoutAccountId`; see ./payout-accounts.ts
 *    for how one is registered). The number was typed once, at registration,
 *    and is kept encrypted in `player.payout_account`, readable only through
 *    the vault; it is on no withdrawal request and in no withdrawal row, which
 *    holds the account's id and the last four digits. A request that could
 *    carry a number is a request a stolen session could point anywhere.
 *
 *  · A second factor AT SIGN-IN. Player sign-in still verifies none —
 *    `mintSession` sets `mfa: false` for every player token — and that stays a
 *    decision rather than a gap. What HAS changed is the half this note used to
 *    call unbuilt: `requestWithdrawalRequest` below now carries `stepUp`, and
 *    the withdraw screen asks for it. See `./player-two-factor.ts` for why the
 *    proof travels ON the withdrawal instead of being remembered on the
 *    session, and why the `mfa` claim is not its input.
 */

/* ── The amount a player types ─────────────────────────────────────────────── */

/**
 * A peso amount as the player entered it, e.g. "500", "1,250.50", "₱75".
 *
 * A STRING, and parsed to BIGINT centavos on the server with `parsePesos` from
 * @dayon/money. It is not a number for the reason the whole platform is not:
 * a JSON number is an IEEE 754 double, `0.1 + 0.2 !== 0.3`, and a two-decimal
 * peso amount put through `Number()` is a reconciliation break waiting for a
 * cashier to chase. The regular expression refuses a third decimal place
 * outright rather than rounding it, because rounding somebody's money silently
 * is how a support ticket starts.
 */
export const pesoAmountInput = z
  .string()
  .trim()
  .min(1, 'Enter an amount.')
  .max(24, 'That is not an amount.')
  .regex(
    /^₱?\s*\d{1,3}(,\d{3})*(\.\d{1,2})?$|^₱?\s*\d+(\.\d{1,2})?$/,
    'Enter pesos, with at most two decimal places — for example 500 or 1250.50.',
  );

/** The id of a `payment.payment_method` row, taken from the list the API served. */
export const paymentMethodId = z.string().min(1, 'Choose a payment method.').max(64);

/* ── The identity gate's codes ──────────────────────────────────────────────── */

/**
 * The `details.refusal` a money route sends beside its FORBIDDEN sentence when the player has not started
 * verifying their identity - the one refusal of the identity gate that verifying clears.
 *
 * The gate (`assertIdentityVerified`, in the API's player-money module) stops a deposit, a withdrawal and the
 * registration of a payout account, and it speaks in four sentences, one for each state a check can be in. All
 * four are FORBIDDEN and all four point to the identity screen, each for its own reason: to start a check (this
 * code), to see where a check in progress is (`IDENTITY_CHECK_IN_PROGRESS_REFUSAL`, below), or to see what the
 * player may do after a refusal (`IDENTITY_REJECTED_REFUSAL`, below). A screen offers the link by the code and
 * never by reading the sentence: reword the sentence and the link silently goes.
 *
 * It is the word a refused GAME LAUNCH already uses for the same fact (`GAME_LAUNCH_REFUSALS`), held to that
 * list by the compiler, so a screen learns one code for "not verified yet".
 */
export const IDENTITY_NOT_VERIFIED_REFUSAL =
  'IDENTITY_NOT_VERIFIED' as const satisfies GameLaunchRefusal;

/**
 * THE LAST CHECK WAS NOT ACCEPTED, AND THE IDENTITY SCREEN SAYS WHAT THE PLAYER MAY DO NEXT — the code beside
 * every refusal of a game, a deposit, a withdrawal or a payout account to a player whose check was refused.
 *
 * ONE CODE, WHETHER THE CASE IS OPEN OR CLOSED. What to fix and how many tries are left change with each reason
 * and each try, so they are said where the player reads about the check — the identity screen and the refusal
 * email — and these refusals point there. Open, that screen says what to fix and takes new photos; closed (a
 * final reason, or the tries spent), it gives the support sentence and a link to support. So the refusal names
 * no reason and no tries, and a final reason is never told apart from a fixable one by it.
 *
 * A game launch uses the same word (`GAME_LAUNCH_REFUSALS`), held to that list by the compiler.
 */
export const IDENTITY_REJECTED_REFUSAL = 'IDENTITY_REJECTED' as const satisfies GameLaunchRefusal;

/**
 * A CHECK IN PROGRESS, AND THE IDENTITY SCREEN SHOWS WHERE IT IS — the code beside every refusal of a game, a
 * deposit, a withdrawal or a payout account while the account's check is under way: with the identity service,
 * with a person, or waiting on the player's own live photo or press. The refusal cannot tell which, so it asks for
 * nothing and states no time; that screen can, and says it.
 *
 * A game launch uses the same word (`GAME_LAUNCH_REFUSALS`), held to that list by the compiler.
 */
export const IDENTITY_CHECK_IN_PROGRESS_REFUSAL =
  'IDENTITY_CHECK_IN_PROGRESS' as const satisfies GameLaunchRefusal;

/**
 * EVERY MONEY REFUSAL WHOSE SENTENCE SENDS THE PLAYER SOMEWHERE, BY CODE - the `details.refusal` beside it.
 *
 * Found on staging on 15 Oct 2026: the deposit popup told a player to open "Verify your identity", or to
 * contact support, and offered nothing but Close. A screen offers the way there by this code and never by
 * reading the sentence, for the reason above: reword the sentence and a link that followed it silently goes.
 * The withdraw and payout-account popups did the same until they read these codes too (review of 807f8c8b).
 *
 *   IDENTITY_NOT_VERIFIED       no check started (the identity gate, every act): "Open "Verify your
 *                               identity" on your profile".
 *   IDENTITY_REJECTED           the last check was refused (the identity gate, every act): ""Verify your
 *                               identity" says what you can do next".
 *   IDENTITY_CHECK_IN_PROGRESS  a check in progress, with the identity service or with a person (the identity
 *                               gate, every act): ""Verify your identity" shows where your check is". The gate
 *                               cannot tell whether the next step is the player's — a live photo to take, a
 *                               link that ran out, a press asking for a person — and that screen can.
 *   OPERATOR_CONTROL            an act the operator's own control refuses - deposits closed on the account
 *                               ("contact support and quote your player reference to have it reviewed"), the
 *                               deposit limit the operator set ("Contact support if you believe this control
 *                               no longer applies"), or withdrawals blocked on the account ("contact support
 *                               and quote your player reference to have it reviewed").
 *   EMAIL_NOT_CONFIRMED         a deposit before the address is confirmed: "you can ask for a new one from your
 *                               profile".
 *   PASSWORD_WITHDRAWAL_LIMIT   a withdrawal authorised with a password, over the limit on how many or how
 *                               much: "Set up an authenticator app on the security screen and this limit no
 *                               longer applies".
 *   PAYOUT_ACCOUNT_REFUSED      an account a reviewer refused, registered again or asked to be retired:
 *                               "contact support if you believe that was wrong".
 *   PAYOUT_ACCOUNTS_UNAVAILABLE no payout account can be registered on this deployment: "Please try again
 *                               later or contact support".
 *
 * A refusal that names nothing the player can do carries no code: a self-exclusion (nobody can lift it
 * early), the player's own limit, and an account already registered or awaiting review. Which screen each code leads to is the player app's
 * (`apps/player-portal/src/lib/refusal-links.ts`), whose table is typed over this list, so a code added here
 * has to be given a way there before it compiles.
 */
export const MONEY_REFUSALS = [
  IDENTITY_NOT_VERIFIED_REFUSAL,
  IDENTITY_REJECTED_REFUSAL,
  IDENTITY_CHECK_IN_PROGRESS_REFUSAL,
  'OPERATOR_CONTROL',
  'EMAIL_NOT_CONFIRMED',
  'PASSWORD_WITHDRAWAL_LIMIT',
  'PAYOUT_ACCOUNT_REFUSED',
  'PAYOUT_ACCOUNTS_UNAVAILABLE',
] as const;
export type MoneyRefusal = (typeof MONEY_REFUSALS)[number];

/* ── Withdrawal ────────────────────────────────────────────────────────────── */

export const requestWithdrawalRequest = z.object({
  methodId: paymentMethodId,
  amount: pesoAmountInput,
  /**
   * WHERE THE MONEY GOES, as the id of one of the player's own registered
   * payout accounts. Not a number: the server looks the account up under the
   * session's player, requires it to be ACTIVE and registered for this rail,
   * and refuses anything else beside this field. An empty choice is refused at
   * the edge, with the sentence below.
   */
  payoutAccountId: z.string().min(1, 'Choose one of your registered payout accounts.').max(64),
  /**
   * THE STEP-UP, AND IT IS REQUIRED.
   *
   * Not optional, and not defaulted to an empty object: a missing `stepUp` is a
   * request that carried no proof, and it must fail at the edge with a sentence
   * about a second factor rather than reach a handler that then has to decide
   * what an absent proof means. The object's own two fields ARE optional
   * because which of them applies is a fact about the account, read from the
   * database inside the withdrawal's transaction — see the note on
   * `withdrawalStepUp` in ./player-two-factor.ts.
   *
   * This field is the reason the note at the head of this file no longer says
   * a withdrawal step-up is unbuilt.
   */
  stepUp: withdrawalStepUp,
});

export type RequestWithdrawalRequest = z.infer<typeof requestWithdrawalRequest>;

/* ── Deposit ───────────────────────────────────────────────────────────────── */

export const createDepositRequest = z.object({
  methodId: paymentMethodId,
  amount: pesoAmountInput,
});

export type CreateDepositRequest = z.infer<typeof createDepositRequest>;

/* ── Reads ─────────────────────────────────────────────────────────────────── */

/**
 * Which movements a player is asking to see.
 *
 * A CLOSED SET, and it is closed for the reason
 * `services/api/src/modules/partner-access/routes.ts` and
 * `packages/reports/src/model.ts` state at length: the value reaches a SQL
 * `kind = $n` comparison, and a caller-chosen value that reaches SQL is only
 * ever safe when the set of values it can take is fixed in code. It is
 * parameterised as well — both, not either.
 */
export const PLAYER_LEDGER_KINDS = [
  'DEPOSIT',
  'WITHDRAWAL',
  'BET',
  'WIN',
  'BONUS_GRANT',
  'BONUS_FORFEIT',
  'ADJUSTMENT',
  'REVERSAL',
] as const;

export type PlayerLedgerKind = (typeof PLAYER_LEDGER_KINDS)[number];

/**
 * A calendar date on the PLATFORM'S clock, not on the caller's.
 *
 * `YYYY-MM-DD`, matched against the shape `@dayon/db`'s `gamingDayInstant`
 * accepts, which is what turns it into an instant. That conversion is the whole
 * reason this is a calendar date and not a timestamp: the gaming day starts at
 * Manila midnight, which is 16:00 the previous day in UTC, and a player who
 * asked for "3 September" and got 16:00Z-to-16:00Z would see the tail of the
 * 2nd and none of their own evening. Every screen in this platform already
 * renders these rows on Manila's clock; the filter has to agree with the
 * labels the player is reading, or the range excludes rows it visibly contains.
 *
 * The regex only asserts the SHAPE. `gamingDayInstant` refuses 2026-02-30 by
 * round-tripping it, and the route turns that refusal into a field message.
 */
const gamingCalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date in the form 2026-09-03.');

export const playerLedgerQuery = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    perPage: z.coerce.number().int().positive().max(100).default(20),
    kind: z.enum(PLAYER_LEDGER_KINDS).optional(),
    /** Inclusive, from the START of this gaming day. */
    from: gamingCalendarDate.optional(),
    /** Inclusive, to the END of this gaming day. */
    to: gamingCalendarDate.optional(),
  })
  .refine((v) => v.from === undefined || v.to === undefined || v.from <= v.to, {
    path: ['to'],
    /*
     * Refused here rather than returned as an empty list. A reversed range is
     * a typo, and "no transactions found" is the one answer a player must not
     * be given for one — on a money screen it reads as "your money is not
     * there". String comparison is sound for a fixed-width ISO date and needs
     * no parsing to be correct.
     */
    message: 'The end of the range is before the start.',
  });

export type PlayerLedgerQuery = z.infer<typeof playerLedgerQuery>;

/* ── The simulated provider callback ───────────────────────────────────────── */

/**
 * What the MOCK payment driver's callback carries.
 *
 * This shape exists so a deposit can be SETTLED by something outside the
 * player's own session, which is what "settles when the provider says so"
 * means. It is registered as a route ONLY when the platform is running the mock
 * driver AND is not in production — `packages/config` already refuses
 * `PAYMENT_DRIVER=MOCK` under NODE_ENV=production, so the two conditions
 * together make it structurally unreachable on a live deployment.
 *
 * `outcome` is a closed set of two, not a boolean: a failure carries a provider
 * error code and a success does not, and `deposit_failure_has_code` is a CHECK
 * that refuses a FAILED row without one.
 */
export const DEPOSIT_CALLBACK_OUTCOMES = ['SETTLED', 'FAILED'] as const;

export const depositCallbackRequest = z
  .object({
    providerRef: z.string().min(1).max(128),
    outcome: z.enum(DEPOSIT_CALLBACK_OUTCOMES),
    /** The provider's own event id — the idempotency key on `webhook_inbox`. */
    eventId: z.string().min(1).max(191),
    errorCode: z.string().min(1).max(32).optional(),
  })
  .refine((v) => v.outcome !== 'FAILED' || v.errorCode !== undefined, {
    path: ['errorCode'],
    message: 'A failed deposit must carry the provider error code that failed it.',
  });

export type DepositCallbackRequest = z.infer<typeof depositCallbackRequest>;

/* ── Shapes the portal reads back ──────────────────────────────────────────── */

/**
 * A payment rail as the player sees it.
 *
 * `min`/`max` are the REAL limits from `payment.payment_method`, narrowed by
 * `payment.payment_method_brand` where that brand sets a tighter pair. They are
 * not copy: the deposit and withdraw screens once printed "₱100–₱50,000" and
 * "1-5 mins" as page text, which is provider configuration this platform did
 * not hold.
 */
export const playerPaymentMethod = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  /** LIVE · DEGRADED — a DISABLED rail is not returned at all. */
  status: z.string(),
  minAmount: centavos,
  maxAmount: centavos,
});

export type PlayerPaymentMethod = z.infer<typeof playerPaymentMethod>;

/**
 * WHAT THE PLAYER HAS NOT SEEN YET.
 *
 * The bell in the header showed a dot that was hard-coded on, over a panel of
 * standing marketing copy. It told every player there was something new, always,
 * and there never was. A player whose payout had just been declined got the same
 * dot as one whose account had done nothing for a month — which makes the dot
 * worth exactly nothing, and is the reason the refusal reached them only because
 * they happened to scroll to the bottom of the withdraw screen.
 *
 * ── DERIVED, NOT DELIVERED ─────────────────────────────────────────────────
 *
 * There is no notification table and this deliberately does not add one. An
 * event row written beside a decision is a second copy of that decision, and
 * two copies drift: the classic failure is a notification saying a payout was
 * declined for a request that was later reversed, because one writer was taught
 * about the reversal and the other was not. These items are READ from the rows
 * that already hold the facts, so a notification cannot outlive or contradict
 * the thing it describes. The cost is that only events with a durable record can
 * appear here, which is the right limit to have.
 *
 * ── FACTS, NOT SENTENCES ───────────────────────────────────────────────────
 *
 * No `title` and no `body`. The player app already owns the wording for a
 * refused payout — a closed map from reason code to plain language, with a
 * per-reason decision about whether the operator's note is shown at all — and a
 * sentence composed here would be a SECOND wording for the same event, free to
 * disagree with the first. The client renders these; the route states what
 * happened.
 *
 * ── WHAT IS NOT IN HERE, AND SAID RATHER THAN DISCOVERED ───────────────────
 *
 * A reply on a support ticket. It is a real thing a player waits on, and it
 * needs "since you last read the thread", which nothing records. Half-answering
 * it — a dot for any ticket with any staff message ever — would light the bell
 * permanently for anybody who has ever contacted support, which is the defect
 * this replaces. The panel says support is not covered rather than implying it.
 *
 * ── THE IDENTITY CHECK'S TWO VERDICTS ──────────────────────────────────────
 *
 * IDENTITY_VERIFIED and IDENTITY_REFUSED, one per decided identity record,
 * current or replaced. They replace KYC_VERIFIED and KYC_REJECTED, which were
 * read from the older KYC process (closed 23 October 2026): the first could
 * never fire, and neither ever said anything about the identity check.
 */
export const playerNotificationKind = z.enum([
  'WITHDRAWAL_DECLINED',
  'WITHDRAWAL_RELEASED',
  'DEPOSIT_SETTLED',
  'DEPOSIT_FAILED',
  'IDENTITY_VERIFIED',
  'IDENTITY_REFUSED',
]);
export type PlayerNotificationKind = z.infer<typeof playerNotificationKind>;

export const playerNotification = z.object({
  /**
   * Stable across reads, and derived from the SOURCE ROW rather than generated:
   * the client marks what it has seen by id, so an id that changed on every
   * fetch would relight the bell forever.
   */
  id: z.string(),
  kind: playerNotificationKind,
  /** When the thing happened — the decision, not the request. */
  occurredAt: z.iso.datetime(),
  /** The player-facing reference, so the item can name what it is about. */
  ref: z.string().nullable(),
  amount: centavos.nullable(),
  /**
   * A code beside the event, and whose code it is depends on the kind:
   *   - a payout: the operator's reason code on a declined one, which the client
   *     turns into words with the map it already holds;
   *   - a deposit: the provider's error code, where the provider gave one;
   *   - an identity check: always null. Its refusal reason is never sent to the
   *     player — a refusal can be a fraud finding, and naming the signal teaches
   *     what to change.
   */
  reasonCode: z.string().nullable(),
});
export type PlayerNotification = z.infer<typeof playerNotification>;
